import { saveAs } from 'file-saver';
import * as Papa from 'papaparse';
import _ from 'lodash';
import {
    channelNameToType,
    channelTypeToName
} from '../const';
import {
    AvsFileName,
    Design,
    DesignChannelName,
    ObjectClass
} from '../types';

import { ExportManager } from './ExportManager';
import Utility from './Utility';
import { getDsids, maybeYieldToMain } from './functions';

/**
 * @module Exporter
 * JSON file exporter
 */
export class Exporter {
    static createJsonBlob(data) {
        return new Blob([JSON.stringify(data)], { type: "application/json" });
    }

    /**
     * @param {Design} serialized
     */
    static async cleanSerialized(serialized, shouldYield = false) {
        await Utility.updatePromptsUsedIn(serialized, shouldYield);
        await maybeYieldToMain(shouldYield);
        Utility.removeDuplicateComponents(serialized);
        await maybeYieldToMain(shouldYield);
    }

    /**
     * @param {Design} serialized
     */
    static getFileName({
        branchName,
        clientName,
        designName,
        version
    }) {
        return [
            clientName,
            designName,
            branchName,
            version
        ].join('-');
    }

    /**
     * @param {Design} serialized
     */
    static getSerializedJson(serialized) {
        return _.omit(serialized, "$loki", "meta", "tabs", "unitTests");
    }

    /**
     * Transform and export properties
     * @param props
     */
    static toCsv(arr) {
        return arr.reduce(function (csvString, row) {
            csvString += row.join(',');
            csvString += "\r\n";  //";";//"\n";
            return csvString;
        }, '');
    }

    /**
     * rename reusbale functions
     * @param {Design} serialized
     */
    static renameReusableFunction(serialized) {
        if (!serialized.functions) {
            return "";
        }

        const designName = serialized.designName.toLowerCase();
        const { functions } = serialized;
        const functionsNames = new Map(functions.map(({ name }) => {
            return [name, `${designName}$${name.toLowerCase()}`];
        }));

        return functions.map(({ script, ...fn }) => {
            const parameters = (fn.parameters ?? []).map(({ key }) => key).join(", ");
            functionsNames.forEach((replaceName, name) => {
                const nameSearch = new RegExp(`${name}(?=\\()` , "g");
                script = script.replace(nameSearch, replaceName);
            });

            return [
                `function ${functionsNames.get(fn.name)}(${parameters}) {`,
                `  /* ID:${fn.id}*/`,
                `  ${script}`,
                "}"
            ].join("\n");
        }).join("\n");
    }

    /**
     * Export designer2-format JSON.
     * @param {Design} serialized
     */
    static async exportDesignerFile(serialized) {
        await this.cleanSerialized(serialized);
        saveAs(
            this.createJsonBlob(this.getSerializedJson(serialized)),
            `${this.getFileName(serialized)}.json`
        );
    }

    /**
     * Export engine-format JSON.
     * @param {Design} serialized
     */
    static async exportEngineFile(serialized) {
        const fileName = this.getFileName(serialized);
        const exportManager = new ExportManager();
        const functionObj = Exporter.renameReusableFunction(serialized);
        const { isOmniChannel } = serialized.omniChannel;

        await Exporter.cleanSerialized(serialized);

        // do not modify serialized when passing to ExportManager, instead pass in a copy of serialized
        const exportObj = await exportManager.getExport(serialized);
        const designObj = this.getSerializedJson(serialized);

        var JSZip = require("jszip");
        var zip = new JSZip();
        var exportFile = zip.folder(fileName, { base64: true });

        // get prompts data for Prompt.csv export
        const promptObjs = serialized.components.objects.filter(obj => obj._class === ObjectClass.prompt);
        const languages = serialized.languages.map((l) => l.language)
        const getRowData = (
          prompt,
          channelName
        ) => {
          const { channel, text } = prompt.props.config ?? {};
          const promptObjs = channel?.[channelNameToType[channelName]] ?? text;
          if (!promptObjs) {
            return [];
          }

          let row = [];

          row.push(prompt.name);
          row.push(prompt.wftName || "");
          row.push(channelName ?? DesignChannelName.voice);
          languages.forEach((lang) => {
            row.push(promptObjs[lang] || "");
          });
          return row;
        };

        const localeExportFields = ["Name", "Workflow Tool Name",'Channel Layer'].concat(languages);
        if (isOmniChannel) {
          const channelNames = Object.keys(
            serialized.rootSettings.channels
          ).map((type) => {
            return channelTypeToName[type];
          });
          for (const channelName of channelNames) {
            const promptsRowData = promptObjs.reduce(
              (acc, prompt) => {
                const row = getRowData(prompt, channelName);
                if (row.length > 0) {
                  return [...acc, row];
                }
                return acc;
              },
              [localeExportFields]
            );

            if (promptsRowData.length > 1) {
              const newCsv = Papa.unparse(promptsRowData);
              exportFile.file(`Prompts_${channelName}.csv`, newCsv);
            }
          }
        } else {
          const promptsRowData = [
            localeExportFields,
            ...promptObjs.map((prompt) => getRowData(prompt)),
          ];
          const newCsv = Papa.unparse(promptsRowData);
          exportFile.file(AvsFileName.prompts, newCsv);
        }

        let inputCsvHeaderFields = ['InputName', 'InputType', 'SamplePrompt', 'SamplePromptText', 'ModuleName', 'Sensitive', 'DTMF'];
        let inputsRowData = [inputCsvHeaderFields];
        let inputObjs = [];
        var Inputs = [];
        window.apm.serialized.components.modules.forEach(module => {
            module.versions[1].flow.nodes.forEach(nod => {
                if (nod._class === ObjectClass.input) {
                    let promObjs = [];
                    for (var li = 0; li < module.versions[1].flow.links.length; li++) {
                        var link = module.versions[1].flow.links[li];
                        if (link.target === nod.id) {
                            var prIndex = module.versions[1].flow.nodes.findIndex(el => el.id === link.source);
                            if (module.versions[1].flow.nodes[prIndex]._class === ObjectClass.prompt) {
                                promObjs.push(module.versions[1].flow.nodes[prIndex]);
                            }
                        }
                    }
                    var inputObjIndex = inputObjs.findIndex(object => object.input.name === nod.name);
                    if (inputObjIndex === -1) { inputObjs.push({ input: nod, prompt: promObjs[0], module: module.name }); }
                    Inputs.push(nod.props.parentComponentID);
                }
            });
        });
        var inputs = _.filter(window.apm.serialized.components.objects, { _class: ObjectClass.input });
        inputs.forEach(input => {
            var inputIndex = Inputs.findIndex(inp => inp === input.id);
            if (inputIndex === -1) {
                inputObjs.push(({ input: input, prompt: undefined, module: undefined }));
            }
        });
        inputObjs.map(obj => {
            let row = [];
            row.push(obj.input.name);
            row.push(obj.input.props.config.inputType);

            if (obj.prompt) {
                row.push(obj.prompt.name);
                row.push(obj.prompt.props.config.text["en-US"]);
                row.push(obj.module);
            }
            else {
                row.push("Unused","","");
            }

            if(obj.input.props.config.sensitiveData) {
                row.push(obj.input.props.config.sensitiveData);
            } else {
                row.push('FALSE');
            }
            if(obj.input.props.config.DTMF) {
                row.push(obj.input.props.config.DTMF);
            } else {
                row.push('FALSE');
            }
            inputsRowData.push(row);
        });
        const newInputCsv = Papa.unparse(inputsRowData);
        const properties = window.apm.serialized.appProperties.filter(p => p.type === "variableBinding");
        let propStr = "";
        if (properties) {
            properties.forEach(p => {
                const variable = window.apm.getVariable(p.variable.split(".")[1]);
                if (variable) {
                    switch (variable.props.config.type) {
                    case "STRING":
                        propStr += `${p.config}=${p.default ? "\"" +p.default + "\"" : ""}\n`;
                        break;
                    case "BOOLEAN":
                        propStr += `${p.config}=${p.default === "false" ? false : !!p.default}\n`;
                        break;
                    default:
                        propStr += `${p.config}=${p.default ? p.default : ""}\n`;
                    }
                }
            });
        }
        const propFile = new Blob([propStr], { type: "text/plain;charset=utf-8" });
        exportFile.file(AvsFileName.inputs, newInputCsv);
        exportFile.file(AvsFileName.export, this.createJsonBlob(exportObj));
        exportFile.file(AvsFileName.design, this.createJsonBlob(designObj));
        exportFile.file(AvsFileName.dsids, this.createJsonBlob(getDsids(serialized)));
        exportFile.file(AvsFileName.appCcxml, serialized.ccxml);
        exportFile.file(`${window.apm.serialized.client}_${window.apm.serialized.client}${window.apm.serialized.application}.properties`.toLowerCase(), propFile);
        exportFile.file(AvsFileName.unitTests, this.createJsonBlob(serialized.unitTests));

        if (functionObj.length > 0) {
            exportFile.file(AvsFileName.functions, functionObj);
        }

        if (exportObj.csvData.length > 0) {
            for (var cf = 0; cf < exportObj.csvData.length; cf++) {
                exportFile.file(designObj.csvData[cf].name + ".csv", designObj.csvData[cf].value);
            }
        }

        zip.generateAsync({
            type: "blob",
            compression: "DEFLATE",
            compressionOptions: {
                level: 5
            }
        }).then(function (content) {
            saveAs(content, `${fileName}.zip`);
        });
    }
}

export default Exporter;
